import { Facility, ISODateString, Timezone, Unit } from "@m7-health/shared-utils";

import { IAttribute, IStaffJob, StaffCategory } from "~/api";
import { ICustomSelectItem } from "~/common/components/TrackedComponents/Select/types";
import { LAvailableMuiIconsClassNames } from "~/common/constants";
import {
  EStaffCategoryKey,
  EStaffCategoryName,
  EStaffTypeKey,
  EStaffTypeName,
  TimeString,
  Uuid,
} from "~/common/types";
import { ITimeTargetLevelsByStaff } from "~/common/types/schedule";
import { THouseViewState } from "~/features/HouseView/store";
import { IUser } from "~/features/User/types";

import { DeprecatedTShift } from "#/features/SchedulerGrid/types";

export enum EShiftTypeDropdownHolder {
  schedulerGrid = "schedulerGrid",
  dailyReport = "dailyReport",
  schedulerGridQuickAction = "schedulerGridQuickAction",
}

export interface IFacility {
  id: Uuid;
  name: string;
  twilioPhoneNumber?: string;
  configuration: Facility.Config.TContent;
  healthSystemId?: string;
}

export interface IGetFacilities {
  data: IFacility[];
}

export interface ICreateUnit {
  scheduleDurationInWeeks: number;
  unitName: string;
  unitTimezone: string;
  facilityId?: string;
}

/** @deprecated - use Unit.TConfiguration from @m7-health/shared-utils instead */
export type TUnitConfiguration = {
  data?: {
    timeTargetLevels?: ITimeTargetLevelsByStaff;
  };
  defaults?: {
    schedulerGrid?: {
      filters?: {
        attributes?: IStaffAttribute["key"][];
        showAssigned?: boolean;
        showEligible?: boolean;
        showTargetLevels?: boolean;
        showTimeTargetLevels?: boolean;
        showShiftTargetLevels?: boolean;
      };
    };
    houseView?: {
      filters?: THouseViewState["findStaffToWork"]["filters"];
    };
  };
  settings?: {
    staffApp?: {
      showAllDropShifts?: boolean;
    };
    schedulerApp?: {
      sortStaffByPrimaryShiftTypeInSchedulerGrid?: boolean;
      useFlexedUpdate?: boolean;
    };
    notifications?: {
      skipStaffNotificationOnOpsIfNotReady?: boolean;
    };
  };
};

/** @deprecated - use Unit.TStaffingLevelMatrix from @m7-health/shared-utils instead */
export type TUnitStaffingLevelMatrix = {
  [patientCount: number]: Partial<{
    [staffCategoryKey in StaffCategory.EKey]: {
      startTime: TimeString;
      endTime: TimeString;
      staffingLevel: number;
    }[];
  }>;
};

export interface IUnitBasic {
  id: string;
  name: string;
  scheduleDurationInWeeks: number;
  timezone: string | Timezone;
  weekendDays: number[];
  configuration: Unit.TConfiguration;
  staffingLevelMatrix: Unit.TStaffingLevelMatrix;
  facility?: IFacility;
  facilityId?: IFacility["id"];
  userIds?: IUser["id"][];
  eligibleStaffJobIds?: IStaffJob["id"][];
  attributes?: IAttribute[];
  archivedAt?: ISODateString | null;
  weekendDaysString?: string;
  staffTypeKeys?: EStaffTypeKey[];
  staffCategoryKeys?: EStaffCategoryKey[];
  createdAt?: ISODateString;
  updatedAt?: ISODateString;
}

export interface IGetUnits {
  data: IUnitBasic[];
}

export interface IGetStaffAttributesForUnit {
  data: IStaffAttribute[];
}

export interface IStaffAttribute {
  key: string;
  name: string;
  abbreviation: string;
  usedForShiftOverview: boolean;
  unitId: string;
}

export interface IUnitCustomSelect extends ICustomSelectItem, IUnitBasic {
  value: string;
  label: string;
}

export interface IEditUnit {
  body: {
    scheduleDurationInWeeks: number;
    unitName: string;
    unitTimezone: string;
    facilityId?: string;
    eligibleStaffJobIds?: IStaffJob["id"][];
  };
  unitId: string;
}

export interface IGetShifts {
  data: IShift[];
}

export interface IShift {
  id?: Uuid;
  key: DeprecatedTShift;
  name: string;
  unitId?: Uuid;
  deletedAt?: string;
  sortPosition?: number;

  // Shift time and duration
  startTime: TimeString;
  durationSeconds: number;

  // --> APPEARANCE
  displayableInDropdowns: EShiftTypeDropdownHolder[];
  printAbbreviation?: string;
  muiIconClassName?: LAvailableMuiIconsClassNames;
  iconColor?: string;
  // Scheduler POV
  scheduleViewDisplayName: string;
  scheduleViewColor: string;
  schedulerAssignableShift: boolean;
  // Staff POV
  staffViewDisplayName: string;
  staffViewColor: string;
  staffPreferableShift: boolean;
  // <--- APPEARANCE

  // Shift description
  isPatientCareShift: boolean;
  isWorkingShift: boolean;
  isPaidShift: boolean;
  isCountedForRealTimeStaffingTarget: boolean;

  // Fairness rules
  violationIfReplacingNull: boolean;
  violationIfReplacedByNull: boolean;
  violationIfReplacedByPatientNonWorking: boolean;
  violationIfReplacedByNonPatientNonWorking: boolean;
  violationIfReplacedByPatientWorking: boolean;
  violationIfReplacedByNonPatientWorking: boolean;

  customJSXelementAsIcon?: JSX.Element;
}

export interface IGetStaffTypes {
  data: {
    key: EStaffTypeKey;
    name: EStaffTypeName;
    usedForTargetLevel: boolean;
    category: string;
  }[];
}

export interface IGetStaffCategories {
  data: { key: EStaffCategoryKey; name: EStaffCategoryName; usedForTargetLevel: boolean }[];
}

export interface IGetStaffTypesAndCategories {
  data: { [key: string]: EStaffTypeName | EStaffCategoryName | undefined };
}

export interface IGetStaffTypeCategoryNameToKey {
  data: { [key: string]: EStaffCategoryKey | EStaffTypeKey };
}
